// The Email Workspace's ATTACHMENTS listing (busy-my-web#403).
//
// Fatemeh's handoff is the Figma SECTION 40011000-380352, "Review email groups and attachments".
// Its stages are: the home page (1), the follow-up that opens the email briefing (2), the
// ATTACHMENTS LISTING in the workspace middle (3 — default, hover, one row selected, several rows
// selected), then what a selection is then used for: "Add to ongoing" (4) and "Save to files" (5).
//
// This module is stage 3's reading: what a row of that listing SAYS, measured off the frame, with
// no DOM anywhere near it. The frame's row is five columns and two trailing actions:
//
//   Name 205 | Size 128 | Kind 82 | Date Added 136 | Related email 119 | ⤓  ↗
//
// The DRAWING (EmailAttachmentsList.vue) has since grown the frame's two into four: View (#481) and,
// since busy-my-web#538 (2026-09-21), Delete — moved onto this row from the viewer's own toolbar,
// which used to delete-then-close as a fourth exit from a screen that should only have two. Neither
// is in the frame this module's numbers are measured off, so they are recorded here rather than in
// the measurements above.
//
// …rows 36 high on a 40px pitch, inside the workspace's own 788px middle — the same geometry the
// group listing above it uses, so the two listings in one middle line up.
//
// WHAT THE PAYLOAD CANNOT SAY (busy-document-srv / busy-main-bff, noted for #403's PR):
// the frame's "Related email" column writes the NAME of the correspondent the file arrived from
// ("Saffet Muefa", "Finance Team"). The documents payload carries `timelineId` and nothing else
// about that conversation — no sender name, no subject — so `attachmentRow` exposes the id (which
// is enough to OPEN the conversation) and leaves the label empty until the BFF sends one.

import { attachmentSize } from './attachmentsBriefing'
import { countLabel } from './briefings/rows'

/** The frame's columns, in px (frame 40011000:380462, "row-title"). */
export const ATTACHMENT_COLUMNS = { name: 205, size: 128, kind: 82, dateAdded: 136, related: 119 }

/** Row geometry: 36 high, 4px between rows (the frame's 40px pitch). */
export const ATTACHMENT_ROW_HEIGHT = 36
export const ATTACHMENT_ROW_GAP = 4

/** The column headings, in the frame's words and its order. */
export const ATTACHMENT_COLUMN_LABELS = ['Name', 'Size', 'Kind', 'Date Added', 'Related email']

// What a file IS, in the words the frame uses ("JPEG Image", "PDF Document", "Word Document"…).
// Keyed by extension because that is what the user sees in the name; the content type is the
// fallback for a file whose name has no extension at all.
const KIND_BY_EXTENSION = {
  jpg: 'JPEG Image',
  jpeg: 'JPEG Image',
  png: 'PNG Image',
  gif: 'GIF Image',
  webp: 'Image',
  svg: 'Image',
  pdf: 'PDF Document',
  doc: 'Word Document',
  docx: 'Word Document',
  xls: 'Excel Spreadsheet',
  xlsx: 'Excel Spreadsheet',
  csv: 'CSV File',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  fig: 'Figma File',
  txt: 'Text File',
  zip: 'Archive',
  eml: 'Email',
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The bit after the last dot, lowercased — '' when the name has no extension. */
export function attachmentExtension(name) {
  const words = String(name || '')
  const dot = words.lastIndexOf('.')
  if (dot <= 0 || dot === words.length - 1) return ''
  return words.slice(dot + 1).toLowerCase()
}

/**
 * The "Kind" column. The extension first, then the content type's own family, then the honest
 * fallback — "File" — rather than a guess at what the bytes are.
 *
 * @param {{name?:string, contentType?:string}} entry
 * @returns {string}
 */
export function attachmentKind(entry) {
  const byExtension = KIND_BY_EXTENSION[attachmentExtension(entry?.name)]
  if (byExtension) return byExtension
  const type = String(entry?.contentType || '').toLowerCase()
  if (type.startsWith('image/')) return 'Image'
  if (type.startsWith('audio/')) return 'Audio'
  if (type.startsWith('video/')) return 'Video'
  if (type.startsWith('text/')) return 'Text File'
  return 'File'
}

/**
 * The "Date Added" column, as the frame writes it: "Aug 22, 2026 at 12:00" — the month and day, the
 * year (these files are years deep), and the time on a 24-hour clock.
 *
 * A date the server did not send is '' rather than "Invalid Date": a blank cell is true, and a
 * broken one is a bug report.
 *
 * @param {string|number|Date|null} value
 * @returns {string}
 */
export function attachmentDateAdded(value) {
  if (value == null || value === '') return ''
  const raw = typeof value === 'string' && !value.endsWith('Z') && !value.includes('+') ? `${value}Z` : value
  const date = raw instanceof Date ? raw : new Date(raw)
  if (isNaN(date.getTime())) return ''
  const time = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} at ${time}`
}

/**
 * The "Related email" column. `id` is the conversation the file arrived on — enough to open it —
 * and `label` is what the frame writes there, which the payload does not yet carry (see the note at
 * the top of this file). Null when the file is not from an email at all.
 *
 * @param {{timelineId?:*, relatedEmail?:string, timelineHeadLine?:string}} entry
 * @returns {{id:*, label:string}|null}
 */
export function attachmentRelatedEmail(entry) {
  const id = entry?.timelineId
  if (!id) return null
  const label = String(entry?.relatedEmail || entry?.timelineHeadLine || '').trim()
  return { id, label }
}

/**
 * One row of the listing, from an `attachmentEntries` entry (lib/attachmentsBriefing.js) — the same
 * reading of a document every other file surface in the product starts from, so the workspace's
 * listing cannot come to disagree with the Briefing's about what a file is called.
 *
 * @param {object} entry
 * @returns {object} the row the template draws
 */
export function attachmentRow(entry) {
  return {
    id: String(entry?.id ?? ''),
    name: entry?.name || 'Attachment',
    size: entry?.sizeLabel || attachmentSize(entry?.size),
    kind: attachmentKind(entry),
    dateAdded: attachmentDateAdded(entry?.lastModified),
    // …and the RAW stamp beside the written one (busy-my-web#481, 2026-09-15 parity audit D-4). The
    // attachment viewer's rail writes the same moment in a different shape for a 296px panel ("Aug
    // 27, 26" over "12:30 PM", `viewerStamp`), and re-parsing this column's English back into a
    // date to do it would be the listing's formatting becoming an input to someone else's.
    addedOn: entry?.lastModified ?? null,
    related: attachmentRelatedEmail(entry),
    contentType: entry?.contentType || '',
  }
}

/** The whole listing, in the order the server sent it. Rows with no id cannot be picked or opened. */
export function attachmentRows(entries) {
  return (entries || []).filter(e => e && e.id).map(attachmentRow)
}

/** A ticked attachment's id on the Work-board — its own namespace, beside `email:` and `email-group:`. */
export function attachmentPickId(id) { return `attachment:${id}` }

/**
 * A ticked attachment, as it goes onto the Work-board.
 *
 * `count` is 0 on purpose: the board's "N Emails" line counts EMAILS, and a file is not one — a
 * selection of five attachments must not read as five emails.
 *
 * @param {object} row a row from {@link attachmentRow}
 * @returns {object}
 */
export function attachmentPick(row) {
  return {
    id: attachmentPickId(row?.id),
    kind: 'attachment-pick',
    attachmentId: row?.id,
    title: row?.name || 'Attachment',
    timelineId: row?.related?.id || null,
    count: 0,
  }
}

/** "1 attachment selected" / "3 attachments selected" — the frame's own line. */
export function attachmentSelectionLabel(count) {
  const n = Number(count) || 0
  return `${n} attachment${n === 1 ? '' : 's'} selected`
}

// ── STAGES 4 AND 5: what a selection of files is then used for (busy-my-web#423) ────────────────
//
// Frames 40011000:380650 / :380706 (ticked rows → the chooser), 40011004:395777 / :396889 ("Add to
// ongoing" → the Possible Ongoing matches pane), 40011008:401490 / :402606 ("Save to Files" → the
// notice, the Organize link and the rail entry).
//
// Stage 3 (the listing above) shipped with #403; the module header already named these two as "what
// a selection is then used for". This is them.

/** Verbatim, in the frames' words. Two spellings are the frames' with their case corrected: the
 * chooser writes "save to Files" and "save to file module" on two frames of the same step, so the
 * option is spelled the way the RESULT notice spells it — "Files", capitalised, as the module is
 * named everywhere else in the product. */
export const ATTACHMENT_TEXT = {
  downloadAll: 'Download all',
  saveToFiles: 'Save to Files',
  addToOngoing: 'Add to ongoing',
  createOngoing: 'Create ongoing',
  // Stage 5's green banner and its two actions.
  saved: 'The attachments has been saved to Files',
  organize: 'Organize',
  undo: 'Undo',
  organizeTooltip: 'Organize in Files',
  // Where "Organize" goes: the Files module's own attachments folder, which is where mail's files
  // already live in this product — not a folder this feature would have to invent.
  organizeRoute: '/files/attachments',
  savedRail: 'Saved to Files',
}

/** The option ids, named once so the chooser, the CTA and the commit cannot drift. */
export const ATTACHMENT_CHOICE = {
  download: 'attachments-download',
  saveFiles: 'attachments-save-files',
  addOngoing: 'attachments-add-ongoing',
  createOngoing: 'attachments-create-ongoing',
}

/** Only the ticked FILES — the board holds groups, threads and research items too. */
export function attachmentPicks(items) {
  return (items || []).filter(i => i && i.kind === 'attachment-pick')
}

/**
 * "2 items selected", with a line per file under it — the Briefing's stage-4 head (40011000:380706).
 *
 * "items", not "attachments": the frame writes items, and the same strip says "3 items selected" of
 * a thread selection one screen earlier. Null with nothing ticked, so the line is absent rather
 * than reading "0 items selected".
 *
 * Each row carries `attachmentId` — the DOCUMENT's own id, not the Work-board's `attachment:<id>`
 * pick id — since busy-my-web#481 / #482 (2026-09-15 parity audit D-4 / D-6). Frames
 * 40011000:380650 and :380706 put a "View ↗" beside every one of these lines, and what View opens
 * is a document; `id` stays the pick id because that is what the row is keyed and unpicked by, and
 * the two ids being one field is exactly how a viewer ends up asking the BFF for "attachment:d1".
 */
export function attachmentSelectionSummary(items) {
  const picks = attachmentPicks(items)
  if (!picks.length) return null
  return {
    // "2 Items selected" — capital after the count, as every Briefing line (busy-my-web#609).
    text: `${countLabel(picks.length, 'item')} selected`,
    rows: picks.map(p => ({
      id: p.id,
      attachmentId: p.attachmentId ? String(p.attachmentId) : '',
      name: p.title,
      timelineId: p.timelineId || null,
    })),
  }
}

// ── The box AT REST over the attachments listing (busy-my-web#482, parity audit D-6) ────────────
//
// Frame 40011000:380432 draws the box under the crumb as four summary lines, each with its own way
// in:
//
//   ⊘ 6 JPEG   View ↗
//   ⊘ 4 Word   View ↗
//   ⊘ 3 PDF    View ↗
//   ⊘ 8 other  View ↗
//
// NOT built from the attachments store's class counts (`countAttachments`), which is the obvious
// place to reach for and the wrong one: those counts are busy-document-srv's CLASSES — documents,
// pdfs, images, invoices, receipts, spreadsheets, presentations — and the frame's words are KINDS,
// which is a finer reading of the same files ("JPEG" and "PNG" are one class and two kinds; "Word"
// and "Text" are one class and two kinds). Summarising classes here would have printed "6 Images ·
// 4 Documents", which is a different sentence from the one Fatemeh drew. So the rows are counted
// off the listing the middle is already showing — the same rows, read a second way rather than
// fetched a second time, which is the rule the listing itself follows (#403).

/** The frame's short word for a kind, keyed by extension. Anything absent is "other". */
const SHORT_KIND = {
  jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', gif: 'GIF', webp: 'WebP', svg: 'SVG',
  pdf: 'PDF',
  doc: 'Word', docx: 'Word',
  xls: 'Excel', xlsx: 'Excel',
  csv: 'CSV',
  ppt: 'PowerPoint', pptx: 'PowerPoint',
  txt: 'Text',
  fig: 'Figma',
  eml: 'Email',
  zip: 'Archive',
}

/** The bucket everything unrecognised falls into — lower-case, as the frame writes it. */
export const OTHER_KIND = 'other'

/**
 * The summary lines, in the frame's order: the named kinds by how many there are, then "other".
 *
 * "other" is LAST whatever its count — the frame has 8 of them above three smaller groups — because
 * it is not a kind, it is the absence of one, and a list of kinds that opens with the leftovers
 * says nothing about what the mailbox is full of.
 *
 * `id` is the first file of the group, which is what its "View ↗" opens (busy-my-web#481): these
 * rows sit in the box of a section whose whole subject is opening a file, and the class chips one
 * level up are already the narrowing gesture — a second one here would be the same control twice.
 *
 * @param {Array} rows rows from {@link attachmentRows}
 * @returns {Array<{key:string, kind:string, count:number, text:string, id:string}>}
 */
export function attachmentKindSummary(rows) {
  const groups = new Map()
  for (const row of rows || []) {
    if (!row?.id) continue
    const kind = SHORT_KIND[attachmentExtension(row.name)] || OTHER_KIND
    if (!groups.has(kind)) groups.set(kind, { key: kind, kind, count: 0, id: String(row.id) })
    groups.get(kind).count += 1
  }
  return [...groups.values()]
    .sort((a, b) => {
      if (a.kind === OTHER_KIND) return 1
      if (b.kind === OTHER_KIND) return -1
      return b.count - a.count || a.kind.localeCompare(b.kind)
    })
    .map(g => ({ ...g, text: `${g.count} ${g.kind}` }))
}

/** "Selected · 2 Attachments" — the Work-board's own heading pair for a file selection. */
export function attachmentBoardSummary(items) {
  const n = attachmentPicks(items).length
  if (!n) return null
  return { heading: 'Selected', count: `${n} Attachment${n === 1 ? '' : 's'}` }
}

/**
 * The four options a file selection is offered (40011000:380706).
 *
 * `ready` is kept honest exactly as the rest of the session keeps it (see lib/emailSession's
 * `suggestions`): an option the design writes but nothing can carry out is SHOWN and marked, never
 * quietly wired to nothing.
 *
 * ALL FOUR ARE READY since 2026-09-15 (parity audit D-5). "Save to Files" was drawn `ready: false`
 * because busy-main-bff's document surface had no copy-a-document-into-my-Files call (checked
 * 2026-09-08 — upload, per-entity list, download and batch list, nothing else). busy-document-srv
 * now has `POST v1/document/copy-to-files` and the BFF proxies it as `POST v1/document/save-to-files`
 * (+ `/undo`), which is exactly the call `SAVE_TO_FILES_PATH` was named for — so, as written then,
 * this is the flag flip and nothing else moved.
 */
export function attachmentActions(items) {
  const picks = attachmentPicks(items)
  if (!picks.length) return []
  return [
    // "Download all" is a verb about a SET (busy-my-web#482, 2026-09-15 parity audit D-6): frame
    // 40011000:380650 has one file ticked and no download row, :380706 has two and does. One file
    // is downloaded from its own row, and "all" of one file is the listing saying nothing.
    ...(picks.length > 1
      ? [{ id: ATTACHMENT_CHOICE.download, text: ATTACHMENT_TEXT.downloadAll, recommended: false, ready: true }]
      : []),
    // NO "Recommended" on any of the four (same audit item): the frame writes four plain radio
    // rows, and the badge the panel puts on its first option was this list inheriting the group
    // chooser's shape. Which of "download / file / connect / create" is right depends entirely on
    // what the files are, so the app has nothing to recommend.
    { id: ATTACHMENT_CHOICE.saveFiles, text: ATTACHMENT_TEXT.saveToFiles, recommended: false, ready: true },
    { id: ATTACHMENT_CHOICE.addOngoing, text: ATTACHMENT_TEXT.addToOngoing, recommended: false, ready: true },
    { id: ATTACHMENT_CHOICE.createOngoing, text: ATTACHMENT_TEXT.createOngoing, recommended: false, ready: true },
  ]
}

/**
 * The conversations a file selection stands for — what "Add to ongoing" and "Create ongoing" file.
 *
 * A document belongs to the thread it arrived on (`timelineId`), and a thread is what every ongoing
 * membership endpoint takes; so filing files means filing the mail they came in on, which is also
 * what keeps the file reachable from the story afterwards. Deduplicated: three files off one email
 * are one conversation, not three.
 */
export function attachmentTimelineIds(items) {
  const seen = new Set()
  for (const pick of attachmentPicks(items)) {
    const id = String(pick.timelineId ?? '').trim()
    if (id) seen.add(id)
  }
  return [...seen]
}

/** The document ids themselves — what a download acts on. */
export function attachmentDocumentIds(items) {
  const seen = new Set()
  for (const pick of attachmentPicks(items)) {
    const id = String(pick.attachmentId ?? '').trim()
    if (id) seen.add(id)
  }
  return [...seen]
}

/** Stage 5's green banner: the sentence, and the two things it offers to do next. */
export function savedToFilesNotice() {
  return {
    kind: 'saved',
    text: ATTACHMENT_TEXT.saved,
    organize: ATTACHMENT_TEXT.organize,
    undo: ATTACHMENT_TEXT.undo,
    route: ATTACHMENT_TEXT.organizeRoute,
  }
}

/**
 * "2 Attachments · Saved to Files (Contactpage.jpeg, Designmockup.fig)" — what the rail keeps.
 *
 * The same receipt shape the archive and the bulk verbs leave (lib/emailSession's `archiveReceipt`),
 * with one row per file, so the timeline has ONE kind of entry for "something happened to these"
 * rather than one per verb.
 */
export function savedToFilesReceipt({ files = [], at = new Date() } = {}) {
  const rows = (files || []).filter(f => f && (f.name || f.id))
  const when = at instanceof Date ? at : new Date(at)
  return {
    id: `attachments-saved:${when.getTime()}`,
    icon: 'folder',
    title: `${rows.length} Attachment${rows.length === 1 ? '' : 's'}`,
    description: ATTACHMENT_TEXT.savedRail,
    date: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(when),
    time: new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: true }).format(when)
      .toLowerCase().replace(/\s?(am|pm)/, ' $1'),
    detail: rows.map(f => f.name).filter(Boolean).join(', '),
    rows: rows.map(f => ({ id: f.id, icon: 'document', text: f.name || 'Attachment' })),
  }
}

export default attachmentRows
